import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, MapPin } from 'lucide-react';
import { historyTimeline, commissions, companyInfo } from '../../data/mock';
import Layout from '../../components/layout/Layout';

const HistoryPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <span>/</span>
              <Link to="/acerca-de/historia" className="hover:text-white transition-colors">Acerca de</Link>
              <span>/</span>
              <span className="text-blue-400">Historia</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Nuestra Historia
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Conoce el recorrido de COPARMEX Manzanillo a lo largo de los años y su compromiso con el desarrollo empresarial de la región.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#1E40AF] text-sm font-semibold uppercase tracking-wider">¿Quiénes Somos?</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mt-3 mb-6">
                {companyInfo.slogan}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                {companyInfo.description}
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Nuestra misión es representar y defender los intereses legítimos de los empresarios mexicanos, promoviendo el desarrollo económico y social del país bajo un marco de responsabilidad social.
              </p>
              <p className="text-gray-600 leading-relaxed">
                En COPARMEX Manzanillo, trabajamos diariamente para fortalecer el tejido empresarial de la región, siendo un puente entre el sector privado y las autoridades gubernamentales.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-[#1E40AF] to-[#0A1628] rounded-2xl overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white p-8">
                    <div className="w-24 h-24 mx-auto mb-6 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                      <span className="text-5xl font-bold">C</span>
                    </div>
                    <h3 className="text-2xl font-bold mb-2">COPARMEX</h3>
                    <p className="text-blue-200">Centro Empresarial Manzanillo</p>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-100 rounded-2xl -z-10" />
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#1E40AF]/10 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gradient-to-br from-[#F8FAFC] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#1E40AF] text-sm font-semibold uppercase tracking-wider">Línea del Tiempo</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mt-3 mb-4">
              Nuestro Recorrido
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Los momentos más importantes en la historia de COPARMEX y nuestro centro empresarial
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-[#1E40AF]/20 hidden lg:block" />

            <div className="space-y-12">
              {historyTimeline.map((item, index) => (
                <div
                  key={index}
                  className={`relative flex flex-col lg:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Content */}
                  <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className={`bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow ${
                      index % 2 === 0 ? 'lg:ml-auto lg:mr-8' : 'lg:mr-auto lg:ml-8'
                    } max-w-lg`}>
                      <span className="inline-block px-4 py-1 bg-[#1E40AF] text-white text-sm font-semibold rounded-full mb-4">
                        {item.year}
                      </span>
                      <h3 className="text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-[#1E40AF] rounded-full border-4 border-white shadow-lg hidden lg:block" />

                  {/* Spacer for opposite side */}
                  <div className="flex-1 hidden lg:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#1E40AF] to-[#0A1628] rounded-2xl p-8 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-6 backdrop-blur-sm">
                <Calendar className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Misión</h3>
              <p className="text-blue-100 leading-relaxed">
                Representar y defender los intereses legítimos de los empresarios, promoviendo el desarrollo económico y social del Puerto de Manzanillo y la región, bajo principios de responsabilidad social, ética empresarial y participación ciudadana activa.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#0A1628] to-[#1E40AF] rounded-2xl p-8 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-6 backdrop-blur-sm">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Visión</h3>
              <p className="text-blue-100 leading-relaxed">
                Ser el organismo empresarial líder en el desarrollo integral de Manzanillo, reconocido por su capacidad de generar valor para sus afiliados, su compromiso con la comunidad y su influencia positiva en las políticas públicas que impulsan el crecimiento económico sostenible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A1628] mb-4">
            Conoce nuestras comisiones de trabajo
          </h2>
          <p className="text-gray-600 mb-8">
            Descubre cómo puedes participar activamente en el desarrollo empresarial de Manzanillo
          </p>
          <Link
            to="/acerca-de/comisiones"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E40AF] text-white font-medium rounded-lg hover:bg-[#1E3A8A] transition-all duration-300"
          >
            Ver Comisiones
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default HistoryPage;
